import React, { useState, useEffect, useCallback, useRef } from 'react';
import { ArrowLeft, RefreshCw, Play, RotateCcw, CheckCircle, Loader, AlertCircle, Clock } from 'lucide-react';
import { GlassCard, GlassButton, GlassBadge } from '../ui';
import {
  getProduction, retryFailedScenes,
  VideoProduction, VideoProductionScene,
} from '../../services/api';
import ProductionPlayer from './ProductionPlayer';
import './VideoProduction.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};

interface Props {
  productionId: number;
  onBack: () => void;
}

const TERMINAL_STATUSES = ['complete', 'failed'];
const IN_PROGRESS_STATUSES = ['generating_scenes', 'assembling'];

const SCENE_STATUS_ICONS: Record<string, React.ReactNode> = {
  pending:    <Clock size={14} />,
  generating: <Loader size={14} className="spin" />,
  complete:   <CheckCircle size={14} style={{ color: 'var(--success, #38a169)' }} />,
  failed:     <AlertCircle size={14} style={{ color: 'var(--danger, #e53e3e)' }} />,
};

const STATUS_CONFIG: Record<string, { label: string; color: 'default' | 'success' | 'warning' | 'danger' | 'info' }> = {
  draft:             { label: 'Draft',      color: 'default'  },
  generating_scenes: { label: 'Generating', color: 'warning'  },
  assembling:        { label: 'Assembling', color: 'info'     },
  complete:          { label: 'Complete',   color: 'success'  },
  failed:            { label: 'Failed',     color: 'danger'   },
};

// Suppress unused import warning — VideoProductionScene is used as a type reference
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type _Scene = VideoProductionScene;

const ProductionDetail: React.FC<Props> = ({ productionId, onBack }) => {
  const [production, setProduction] = useState<VideoProduction | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retrying, setRetrying] = useState(false);
  const [showPlayer, setShowPlayer] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const data = await getProduction(productionId);
      setProduction(data);
      setError('');
      return data;
    } catch (e: any) {
      setError(getApiError(e, 'Failed to load production'));
      return null;
    } finally {
      setLoading(false);
    }
  }, [productionId]);

  // Poll every 10s while in-progress
  useEffect(() => {
    load().then(data => {
      if (data && IN_PROGRESS_STATUSES.includes(data.status)) {
        pollRef.current = setInterval(async () => {
          const updated = await load();
          if (updated && TERMINAL_STATUSES.includes(updated.status)) {
            if (pollRef.current) {
              clearInterval(pollRef.current);
              pollRef.current = null;
            }
          }
        }, 10000);
      }
    });

    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [load]);

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await retryFailedScenes(productionId);
      await load();
    } catch {
      // ignore
    } finally {
      setRetrying(false);
    }
  };

  if (loading) return <p className="vp-empty-text">Loading production…</p>;
  if (error || !production) return <p className="vp-error-text">{error || 'Production not found'}</p>;

  const cfg = STATUS_CONFIG[production.status] || STATUS_CONFIG.draft;
  const isComplete = production.status === 'complete';
  const isFailed = production.status === 'failed';
  const progress = production.total_scenes
    ? Math.round((production.scenes_complete / production.total_scenes) * 100)
    : 0;
  const hasFailedScenes = production.scenes?.some(s => s.status === 'failed');

  const formatDate = (iso: string) =>
    new Date(iso).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  if (showPlayer && production.final_url) {
    return (
      <ProductionPlayer
        production={production}
        onBack={() => setShowPlayer(false)}
      />
    );
  }

  return (
    <div className="vpd-root">
      {/* Header */}
      <div className="vpd-header">
        <GlassButton variant="ghost" onClick={onBack}>
          <ArrowLeft size={16} /> Back
        </GlassButton>
        <div className="vpd-title-row">
          <h2 className="vpd-title">{production.title}</h2>
          <GlassBadge variant={cfg.color}>{cfg.label}</GlassBadge>
        </div>
        <div className="vpd-actions">
          <GlassButton variant="ghost" onClick={() => load()} title="Refresh">
            <RefreshCw size={16} />
          </GlassButton>
          {isComplete && production.final_url && (
            <GlassButton variant="primary" onClick={() => setShowPlayer(true)}>
              <Play size={16} /> Play Video
            </GlassButton>
          )}
          {isFailed && hasFailedScenes && (
            <GlassButton variant="secondary" onClick={handleRetry} disabled={retrying}>
              <RotateCcw size={16} /> {retrying ? 'Retrying…' : 'Retry Failed Scenes'}
            </GlassButton>
          )}
        </div>
      </div>

      {/* Meta */}
      <GlassCard className="vpd-meta-card">
        <div className="vpd-meta-grid">
          <div className="vpd-meta-item">
            <span className="vpd-meta-label">Aspect Ratio</span>
            <span className="vpd-meta-value">{production.aspect_ratio}</span>
          </div>
          <div className="vpd-meta-item">
            <span className="vpd-meta-label">Total Scenes</span>
            <span className="vpd-meta-value">{production.total_scenes}</span>
          </div>
          <div className="vpd-meta-item">
            <span className="vpd-meta-label">Created</span>
            <span className="vpd-meta-value">{formatDate(production.created_at)}</span>
          </div>
          {production.completed_at && (
            <div className="vpd-meta-item">
              <span className="vpd-meta-label">Completed</span>
              <span className="vpd-meta-value">{formatDate(production.completed_at)}</span>
            </div>
          )}
        </div>
      </GlassCard>

      {/* Progress */}
      {production.status === 'generating_scenes' && (
        <GlassCard className="vpd-progress-card">
          <div className="vpd-progress-header">
            <span className="vpd-progress-title">
              <Loader size={16} className="spin" /> Generating scenes…
            </span>
            <span className="vpd-progress-count">{production.scenes_complete} / {production.total_scenes}</span>
          </div>
          <div className="vp-progress-bar">
            <div className="vp-progress-fill" style={{ width: `${progress}%` }} />
          </div>
          <p className="vpd-progress-note">Each scene is being generated individually. This may take a few minutes.</p>
        </GlassCard>
      )}

      {production.status === 'assembling' && (
        <GlassCard className="vpd-progress-card">
          <div className="vpd-progress-header">
            <span className="vpd-progress-title">
              <Loader size={16} className="spin" /> Assembling final video in the cloud…
            </span>
          </div>
          <p className="vpd-progress-note">All scenes have been generated. Stitching, colour grading, and audio mixing in progress.</p>
        </GlassCard>
      )}

      {isFailed && production.error && (
        <GlassCard className="vpd-error-card">
          <AlertCircle size={16} />
          <p>{production.error}</p>
        </GlassCard>
      )}

      {/* Scenes grid */}
      {production.scenes && production.scenes.length > 0 && (
        <GlassCard className="vpd-scenes-card">
          <h4 className="vpd-scenes-title">Scenes</h4>
          <div className="vpd-scenes-grid">
            {production.scenes.map((scene, i) => (
              <div key={i} className={`vpd-scene-tile vpd-scene-${scene.status}`} title={scene.error || scene.prompt}>
                <div className="vpd-scene-tile-num">{scene.scene_index + 1}</div>
                <div className="vpd-scene-tile-icon">{SCENE_STATUS_ICONS[scene.status] || SCENE_STATUS_ICONS.pending}</div>
                <div className="vpd-scene-tile-dur">{scene.duration}s</div>
              </div>
            ))}
          </div>
        </GlassCard>
      )}
    </div>
  );
};

export default ProductionDetail;
